import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { catchError, startWith, switchMap } from 'rxjs/operators';

import { AppConfig } from '../config/app-config';
import { authService } from '../auth/auth.service';
import { NotificationPreferences, UnreadNotificationCount, UserNotification } from '@/app/shared/models/notifications.models';

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private readonly baseUrl = `${AppConfig.apiBaseUrl}/api`;
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private pollingSub?: Subscription;

  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(private readonly http: HttpClient) {}

  getNotifications(page: number, pageSize: number): Observable<UserNotification[]> {
    return this.http.get<UserNotification[]>(`${this.baseUrl}/notifications`, {
      params: { page, pageSize },
    });
  }

  getUnreadCount(): Observable<UnreadNotificationCount> {
    return this.http.get<UnreadNotificationCount>(`${this.baseUrl}/notifications/unread-count`);
  }

  getNotificationPreferences(): Observable<NotificationPreferences> {
    return this.http.get<NotificationPreferences>(`${this.baseUrl}/notifications/preferences`);
  }

  updateNotificationPreferences(preferences: NotificationPreferences): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/notifications/preferences`, preferences);
  }

  markAsRead(userNotificationId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/notifications/${userNotificationId}/read`, {});
  }

  markAllAsRead(): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/notifications/read-all`, {});
  }

  refreshUnreadCount(): void {
    if (!authService.isAuthenticated()) {
      this.unreadCountSubject.next(0);
      return;
    }

    this.getUnreadCount().subscribe({
      next: (result) => this.unreadCountSubject.next(result?.unreadCount ?? 0),
      error: () => {},
    });
  }

  startUnreadPolling(): void {
    if (!authService.isAuthenticated()) {
      this.stopUnreadPolling();
      this.unreadCountSubject.next(0);
      return;
    }

    if (this.pollingSub) return;

    this.pollingSub = interval(60_000)
      .pipe(
        startWith(0),
        switchMap(() => authService.isAuthenticated()
          ? this.getUnreadCount().pipe(catchError(() => of({ unreadCount: this.unreadCountSubject.value })))
          : of({ unreadCount: 0 })),
      )
      .subscribe((result) => this.unreadCountSubject.next(result.unreadCount ?? 0));
  }

  stopUnreadPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  setUnreadCount(count: number): void {
    this.unreadCountSubject.next(Math.max(0, count));
  }

  decrementUnreadCount(): void {
    this.setUnreadCount(this.unreadCountSubject.value - 1);
  }

  ngOnDestroy(): void {
    this.stopUnreadPolling();
  }
}
